// Blog: May 2026 spring cleaning update — refreshed experience for streamers and their communities.
import { defineDocument } from '../../schema';
export const blogSpringCleaningRefreshingTheAirheadGamesExperienceDocument = defineDocument({
    id: 'blog:spring-cleaning-refreshing-the-airhead-games-experience',
    kind: 'blog',
    slug: 'spring-cleaning-refreshing-the-airhead-games-experience',
    route: '/blog/spring-cleaning-refreshing-the-airhead-games-experience',
    seo: {
        title: 'Spring Cleaning: Refreshing the Airhead Games Experience',
        description: 'Platform-wide improvements to help streamers build stronger communities',
        imageAsset: 'gameLibraryCozy',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Airhead Blog',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Spring Cleaning: Refreshing the Airhead Games Experience',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Platform-wide improvements to help streamers build stronger communities',
            },
        ],
        imageAsset: 'gameLibraryCozy',
        imageAlt: 'Game library interface on Airhead Games',
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-05-11T00:00:00.000Z'),
        tags: ['features'],
    },
    sections: [
        {
            id: 'article',
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Spring is a time for renewal and fresh starts, so we opened the windows and dusted off the neglected surfaces of our platform. Over the last few weeks we tidied up the entire place and added polish to the hosting and playing experience. Our mission is to help streamers build stronger communities and, above all, we wanted to make that clearer through our messaging, host controls, and game experience.',
                        },
                    ],
                },
            ],
            sections: [
                {
                    id: 'article__welcome-home',
                    title: [
                        {
                            type: 'text',
                            text: 'Welcome Home',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'The refreshed ',
                                },
                                {
                                    type: 'link',
                                    href: '/',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'homepage',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: " puts streamers front and center in how we introduce Airhead Games. Streamers have always been at the heart of what we do, but it didn't shine through in the way we wanted. Now, landing on our site properly celebrates the streamers and communities who play our games.",
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'In addition to splashier visuals, the homepage was designed with awesome scroll effects to help communicate our story in an engaging way. Although it was a challenge to build, we believe this fun presentation better represents who we are and what we care about.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__the-library-door-is-open',
                    title: [
                        {
                            type: 'text',
                            text: 'The Library Door is Open',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'In this update we tore down all of the barriers to entering our ',
                                },
                                {
                                    type: 'link',
                                    href: '/library',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Game Library',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: `. Streamers considering Airhead Games can now more easily understand our games, `,
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/user-preferences',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'customizable preferences',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', and moderation tools before ever logging in.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'The new Game Library sidebar makes it easy to navigate, adjust preferences, and reload ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/loons',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Loons',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' on the fly. We noticed many streamers incorporate different host and content styles into their sessions, so we made it simple to update these settings without leaving the Game Library screen. Fewer scavenger hunts, more time playing with your community!',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__clearer-controls-for-hosts',
                    title: [
                        {
                            type: 'text',
                            text: 'Clearer Controls for Hosts',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'New and improved ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/moderator-tools#censorship',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'censorship controls',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' allow hosts to moderate their game rooms in the exact way they want. Streamers can choose to filter player inputs using our multilingual library of common profanity or a custom library of words they curate. Common and custom profanity filters can be deployed in tandem or independently, protecting all playing surfaces including player names and game inputs. We are also introducing Rigid Mode to give hosts the option to more aggressively filter profanity hidden inside other words.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: `The improved host controls don't stop at moderation tools: we standardized default volume levels and improved the `,
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/audio',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'audio control interface',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', too. In every content library (games, ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/music',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'music',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/clips',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'clips',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', etc.) we introduced filtering and sorting capabilities to make discovery a breeze.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'In-game host controls also got a makeover with a refreshed ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/poppy#in-game-menu',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Poppy Menu',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' and relocation of the “Skip Intro” button. Skip Intro is now always next to Poppy instead of in the bottom right corner where many streamers place their facecams.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__play-with-confidence',
                    title: [
                        {
                            type: 'text',
                            text: 'Play With Confidence',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: `We left no surface untouched as part of our spring cleaning. We methodically went through every game to improve the player experience. We addressed feedback from the `,
                                },
                                {
                                    type: 'link',
                                    href: 'https://discord.gg/rfFTVAqHwH',
                                    children: [
                                        {
                                            type: 'text',
                                            text: `Poppy's Pals community`,
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', standardized control layouts, and clarified instructions and transition messages. The result is a more intuitive gameplay experience with a familiar look and feel.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'The platform was upgraded with strengthened security, improved navigation, and overall better performance. We are fully committed to refining these details because we understand streamers have their reputation on the line every time they host.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: `We're excited to welcome you to the new Airhead Games experience and hope you feel the love we put into these upgrades. `,
                                },
                                {
                                    type: 'link',
                                    href: '/library',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Open the Game Library',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: `, take it for a spin, and let us know what you enjoy most about our cleaned-up style.`,
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'article-actions',
            widgets: [
                {
                    type: 'relatedDocuments',
                    limit: 4,
                },
            ],
        },
    ],
});
//# sourceMappingURL=spring-cleaning-refreshing-the-airhead-games-experience.js.map